import type { AppMode, SpecialType } from '@iheartjane/types';
import { useInfiniteQuery } from '@tanstack/react-query';

import { fetchStoreSpecials } from './storeSpecials.api';
import type { StoreSpecialsResponse } from './storeSpecials.types';

const STORE_SPECIALS_BASE_KEY = 'storeSpecials';

const DEFAULT_PER_PAGE = 16;

const storeSpecialsQueryKey = (
  storeId: string | number,
  perPage: number,
  appMode: AppMode,
  specialType?: SpecialType | SpecialType[],
  includeHidden?: boolean
) =>
  [
    STORE_SPECIALS_BASE_KEY,
    storeId,
    perPage,
    specialType,
    appMode,
    includeHidden,
  ] as const;

interface UseStoreSpecialsProps {
  appMode: AppMode;
  enabled?: boolean;
  includeHidden?: boolean;
  perPage?: number;
  specialType?: SpecialType | SpecialType[];
  storeId: string | number;
}

export const useStoreSpecials = ({
  appMode,
  enabled = true,
  includeHidden,
  perPage = DEFAULT_PER_PAGE,
  specialType,
  storeId,
}: UseStoreSpecialsProps) =>
  useInfiniteQuery({
    queryKey: storeSpecialsQueryKey(
      storeId,
      perPage,
      appMode,
      specialType,
      includeHidden
    ),
    queryFn: async ({ pageParam }) => {
      const result = await fetchStoreSpecials({
        appMode,
        includeHidden,
        page: pageParam,
        perPage,
        specialType,
        storeId,
      });
      return { ...result, pageParam };
    },
    initialPageParam: 1,
    getNextPageParam: (
      lastPage: StoreSpecialsResponse & { pageParam: number }
    ) =>
      lastPage.specials.length < perPage ? undefined : lastPage.pageParam + 1,
    enabled: enabled && !!storeId,
  });
